import React, { useState, useEffect, useRef } from 'react';
import { getGlobalChecklists, GlobalChecklistQuery } from '../../../api/checklistsApi';
import ChecklistModal from '../../../components/modal/checklist/ChecklistModal';
import type { ChecklistType } from '../../../types/checklist';
import { checklistTypeLabel } from '../../../types/checklist';
import Filter from '../../../img/filter.svg';
import ArrowLeftOrange from '../../../img/arrow-left-orange.svg';
import ArrowLeftGray from '../../../img/arrow-left-gray.svg';
import ArrowRightOrange from '../../../img/arrow-right-orange.svg';
import ArrowRightGray from '../../../img/arrow-right-gray.svg';
import SearchIcon from '../../../img/search-icon.svg';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import '../../../components/como-funciona/help-circle-btn.css';
// Esqueleto de página compartilhado (`recepcao-conte-do`, `recepcao-title`, `recepcao-header-row`).
// O import é explícito de propósito: contar com o CSS carregado por outra tela é acoplamento
// invisível — a tela quebraria se a Recepção deixasse de ser importada (lição do BUG-V74/at-67).
import '../../recepcao/pages/Recepcao.css';
import './ChecklistHistoryPage.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

interface ChecklistHistoryPageProps {
  sidebarRef?: React.RefObject<HTMLDivElement>;
}

/**
 * Rótulo da coluna "Status" a partir do dado, não de texto fixo (at-65, BUG-V72).
 *
 * ⚠️ Hoje `ChecklistSessionStatus` tem **um único valor** (`COMPLETED`), então o texto
 * fixo anterior nunca esteve errado — a troca é para a coluna deixar de ser decorativa
 * e passar a dizer a verdade se o enum ganhar outro estado. Status desconhecido aparece
 * como veio, em vez de ser exibido como "Concluído".
 */
const STATUS_LABELS: Record<string, string> = {
  COMPLETED: 'Concluído',
};

const statusLabel = (status?: string): string => {
  const chave = String(status || '').toUpperCase();
  return STATUS_LABELS[chave] || status || '-';
};

const statusSlug = (status?: string): string =>
  String(status || 'desconhecido').toLowerCase() === 'completed'
    ? 'concluido'
    : String(status || 'desconhecido').toLowerCase();

interface ChecklistItem {
  id: string;
  carId: string;
  type: ChecklistType;
  status: string;
  createdAt: string;
  completedAt?: string | null;
  car: { plate: string; model: string };
  createdBy?: { fullName: string } | null;
}

const ChecklistHistoryPage: React.FC<ChecklistHistoryPageProps> = ({ sidebarRef }) => {
  // Query filters state
  const [plate, setPlate] = useState('');
  const [type, setType] = useState<any>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);

  // Data state
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Selected checklist state for viewing
  const [selectedCarId, setSelectedCarId] = useState<string | null>(null);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [selectedSessionType, setSelectedSessionType] = useState<ChecklistType | null>(null);
  const [isChecklistOpen, setIsChecklistOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  const helpSections: ComoFuncionaSecao[] = [
    {
      titulo: 'O que esta tela mostra',
      conteudo: (
        <p>
          Todos os checklists concluídos da oficina, de todos os veículos — inclusive de atendimentos já entregues.
          Cada linha é um checklist, com o tipo (<strong>Entrada</strong>, <strong>Saída</strong>,{' '}
          <strong>Reparo</strong> ou <strong>Retorno</strong>), quem o preencheu (<strong>Operador</strong>) e a
          data de início do preenchimento. Um checklist só existe aqui depois de concluído — não há rascunho
          salvo à parte.
        </p>
      ),
    },
    {
      titulo: 'Consultar e filtrar',
      conteudo: (
        <>
          <p>
            A busca do topo pesquisa por <strong>placa</strong>. Em <strong>Filtrar</strong>, é possível combinar
            placa, tipo de checklist e o período do preenchimento (<strong>De (Data)</strong> /{' '}
            <strong>Até (Data)</strong>). Os filtros só são aplicados ao confirmar — <strong>Aplicar Filtros</strong>,
            Enter ou a lupa — e <strong>Limpar Filtros</strong> desfaz tudo.
          </p>
          <p>
            Os tipos disponíveis no filtro são <strong>Entrada</strong>, <strong>Saída</strong>,{' '}
            <strong>Entrada de pós-evento</strong> e <strong>Saída de pós-evento</strong>. Os dois últimos são as
            vistorias do <strong>retorno de garantia</strong> — quando o veículo já entregue volta para avaliação
            do serviço. Os tipos antigos Reparo e Retorno deixaram de existir: nunca chegaram a ser usados.
          </p>
        </>
      ),
    },
    {
      titulo: 'Abrir, PDF e compartilhamento',
      conteudo: (
        <>
          <p>
            O clique na <strong>placa</strong> abre o checklist em modo de consulta. <strong>Baixar PDF</strong>{' '}
            gera o documento do veículo — quando existe checklist de saída, o PDF é unificado (entrada + imagens de
            saída).
          </p>
          <p>
            O botão do <strong>WhatsApp</strong> gera um link público para o cliente: o acesso pede o telefone do
            proprietário cadastrado no veículo e o link expira em 30 dias. Sem telefone cadastrado, o
            compartilhamento fica indisponível — cadastre-o na edição do veículo.
          </p>
        </>
      ),
    },
    {
      titulo: 'Edição de checklist concluído',
      conteudo: (
        <p>
          O botão <strong>Editar checklist</strong> só aparece para os perfis autorizados pelo administrador (em
          Administração do Sistema → Checklist → Permissões de Edição). Toda edição — itens, observações, mídias
          adicionadas ou excluídas — é registrada e aparece na tela de <strong>Auditoria</strong> do veículo, com o
          antes e depois.
        </p>
      ),
    },
  ];
  const filterModalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedNode = event.target as Node;
      const isOutsideModal = filterModalRef.current && !filterModalRef.current.contains(clickedNode);
      const isOutsideSidebar = sidebarRef?.current && !sidebarRef.current.contains(clickedNode);

      if (isOutsideModal && isOutsideSidebar) {
        setIsFilterModalOpen(false);
      }
    };

    if (isFilterModalOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFilterModalOpen, sidebarRef]);

  // Recarrega somente quando a paginação muda. Os filtros (placa, tipo, datas) são
  // aplicados de forma explícita pelo usuário (botão de busca, Enter ou modal de filtros),
  // por isso 'fetchChecklists' fica fora das dependências: incluí-lo faria a lista
  // recarregar a cada tecla digitada no campo de placa.
  useEffect(() => {
    fetchChecklists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, limit]);

  const fetchChecklists = async () => {
    try {
      setLoading(true);
      setError(null);
      const query: GlobalChecklistQuery = {
        page: currentPage,
        limit,
        plate: plate ? plate : undefined,
        type: type ? type : undefined,
        startDate: startDate ? startDate : undefined,
        endDate: endDate ? endDate : undefined,
      };

      const result = await getGlobalChecklists(query);
      setItems(result.items);
      setTotal(result.total);
    } catch (err: any) {
      setError(err?.message || 'Erro ao carregar histórico de checklists.');
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setPlate('');
    setType('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
    // Fetch immediately after clearing values
    setTimeout(() => {
      void fetchChecklists();
    }, 0);
  };

  const handleViewChecklist = (carId: string, sessionId: string, type: ChecklistType) => {
    setSelectedCarId(carId);
    setSelectedSessionId(sessionId);
    setSelectedSessionType(type);
    setIsChecklistOpen(true);
  };

  const totalPages = Math.ceil(total / limit) || 1;

  return (
    <>
    <div 
      className="Frame1000004012"
      style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        backgroundColor: 'transparent',
      }}
    >
      <div className="ConteDo recepcao-conte-do">
        <div className="checklist-history-container">
          
          {/* Page Header */}
          <div className="recepcao-header-row">
            <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              Checklists Realizados
              <button
                type="button"
                className="help-circle-btn"
                title="Como Funciona"
                onClick={() => setIsHelpOpen(true)}
              >
                ?
              </button>
            </div>
            <div className="recepcao-search-bar">
              <div 
                className="ItemSearch" 
                onClick={() => {
                  setCurrentPage(1);
                  void fetchChecklists();
                }}
              >
                <img
                  className="imgIcon"
                  src={SearchIcon}
                  alt="Ícone de busca"
                  width="20"
                  height="20"
                />
              </div>
              <input
                type="text"
                value={plate}
                onChange={(e) => setPlate(e.target.value.toUpperCase())}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setCurrentPage(1);
                    // Add a tiny delay to ensure state has updated before fetching
                    setTimeout(() => {
                      void fetchChecklists();
                    }, 0);
                  }
                }}
                placeholder="Pesquise por placa"
                className="recepcao-search-input"
              />
            </div>
          </div>

          {/* Action Toolbar */}
          <div className="checklist-history-toolbar" style={{ display: 'flex', gap: 12, marginBottom: 30, alignItems: 'center', flexWrap: 'wrap' }}>
            <div className="CtaBig MouseHover" onClick={() => setIsFilterModalOpen(true)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', backgroundColor: 'var(--tema-superficie, white)', borderRadius: 8, border: '1px solid var(--tema-linha-2, #CACACA)', padding: '10px 20px', height: 'fit-content' }}>
              <div className="Frame1000003987" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <img className="ComponentNameImgIcon" src={Filter} alt="Filtro" style={{ width: 16, height: 16 }} />
                <div className="ComponentName" style={{ fontFamily: 'AmpleSoft-Bold', fontSize: 14 }}>Filtrar</div>
              </div>
            </div>
          </div>

          <div className="QuantidadeDeCarros">
            <div
              className="Frame1000004037"
              style={{
                alignSelf: 'stretch',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: 12,
                display: 'flex',
              }}
            >
              <div className="CtaQuantidadeDeCarros">
                <div className="Frame1000003987">
                  <div className="ComponentName">
                    {loading ? (
                      'Carregando...'
                    ) : error ? (
                      error
                    ) : total > 0 ? (
                      `${(currentPage - 1) * limit + 1}-${Math.min(currentPage * limit, total)} de ${total} checklists`
                    ) : (
                      'Nenhum checklist'
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Table */}
          <div className="checklist-history-table-card">
            <div className="table-responsive-wrapper">
              <table className="checklist-history-table">
                <thead>
                  <tr>
                    <th>Placa</th>
                    <th>Modelo</th>
                    <th>Tipo</th>
                    <th>Status</th>
                    <th>Operador</th>
                    <th>Data de Início</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={6} className="table-row-state-msg">
                        Carregando histórico...
                      </td>
                    </tr>
                  )}
                  {!loading && error && (
                    <tr>
                      <td colSpan={6} className="table-row-state-msg error-msg">
                        {error}
                      </td>
                    </tr>
                  )}
                  {!loading && !error && items.length === 0 && (
                    <tr>
                      <td colSpan={6} className="table-row-state-msg">
                        Nenhum checklist encontrado para a busca atual.
                      </td>
                    </tr>
                  )}
                  {!loading && !error && items.map((item) => (
                    <tr key={item.id}>
                      <td className="plate-cell" onClick={() => handleViewChecklist(item.carId, item.id, item.type)}>
                        {item.car?.plate || '-'}
                      </td>
                      <td>{item.car?.model || '-'}</td>
                      <td>
                        <span
                          className={`type-badge type-${String(item.type || '').toLowerCase()}`}
                        >
                          {checklistTypeLabel[item.type] || item.type}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge status-${statusSlug(item.status)}`}>
                          <span className="status-dot"></span>
                          {statusLabel(item.status)}
                        </span>
                      </td>
                      <td>{item.createdBy?.fullName || '-'}</td>
                      <td>{new Date(item.createdAt).toLocaleString('pt-BR')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {!loading && !error && total > 0 && (
              <div className="PaginaO">
                <div
                  className={`Frame1000003950 ${currentPage === 1 ? 'disabled' : ''}`}
                  onClick={() => {
                    if (currentPage > 1) {
                      setCurrentPage(currentPage - 1);
                    }
                  }}
                  style={{ cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
                >
                  <img
                    className="imgIcon"
                    src={currentPage === 1 ? ArrowLeftGray : ArrowLeftOrange}
                    alt="Seta para Esquerda"
                    width="24"
                    height="24"
                  />
                  <div
                    className="Anterior"
                    style={{ color: currentPage === 1 ? '#BEBEBE' : 'var(--tema-texto, #1A1C1B)' }}
                  >
                    Anterior
                  </div>
                </div>
                
                <div className="Group21">
                  <div className="Group20">
                    {(() => {
                      if (totalPages === 1) {
                        return (
                          <div key={1} className="page-number active">
                            1
                            <div className="Ellipse14" />
                          </div>
                        );
                      }

                      const pages = [];
                      const maxVisiblePages = 10;

                      pages.push(
                        <div
                          key={1}
                          className={`page-number ${currentPage === 1 ? 'active' : ''}`}
                          onClick={() => setCurrentPage(1)}
                        >
                          1
                          {currentPage === 1 && <div className="Ellipse14" />}
                        </div>
                      );

                      if (currentPage > maxVisiblePages - 4) {
                        pages.push(<span key="left-ellipsis" className="ellipsis">...</span>);
                      }

                      const startPage = Math.max(
                        2,
                        Math.min(
                          currentPage - Math.floor((maxVisiblePages - 2) / 2),
                          totalPages - (maxVisiblePages - 2)
                        )
                      );
                      const endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 3);

                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <div
                            key={i}
                            className={`page-number ${currentPage === i ? 'active' : ''}`}
                            onClick={() => {
                              setCurrentPage(i);
                            }}
                          >
                            {i}
                            {currentPage === i && <div className="Ellipse14" />}
                          </div>
                        );
                      }

                      if (endPage < totalPages - 1) {
                        pages.push(<span key="right-ellipsis" className="ellipsis">...</span>);
                      }

                      pages.push(
                        <div
                          key={totalPages}
                          className={`page-number ${currentPage === totalPages ? 'active' : ''}`}
                          onClick={() => setCurrentPage(totalPages)}
                        >
                          {totalPages}
                          {currentPage === totalPages && <div className="Ellipse14" />}
                        </div>
                      );

                      return pages;
                    })()}
                  </div>
                </div>

                <div
                  className={`Frame1000003949 ${currentPage === totalPages ? 'disabled' : ''}`}
                  onClick={() => {
                    if (currentPage < totalPages) {
                      setCurrentPage(currentPage + 1);
                    }
                  }}
                  style={{ cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
                >
                  <div
                    className="PrXima"
                    style={{ color: currentPage === totalPages ? '#BEBEBE' : 'var(--tema-texto, #1A1C1B)' }}
                  >
                    Próxima
                  </div>
                  <img
                    className="imgIcon"
                    src={currentPage === totalPages ? ArrowRightGray : ArrowRightOrange}
                    alt="Seta para Direita"
                    width="24"
                    height="24"
                  />
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>

    {/* Modal Filtros */}
    {isFilterModalOpen && (
      <div className="filter-checklist-modal-overlay">
        <div className="filter-checklist-modal-content" ref={filterModalRef}>
          <div className="filter-checklist-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', background: 'var(--tema-superficie-2, #f8f9fa)', borderBottom: '1px solid var(--tema-linha, #e9ecef)' }}>
            <h2 style={{ margin: 0, fontSize: 18, fontFamily: 'AmpleSoft-Bold', color: 'var(--tema-texto, #212529)' }}>Filtrar Checklists</h2>
            <button 
              type="button" 
              onClick={() => setIsFilterModalOpen(false)} 
              className="close-button"
              style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--tema-texto-3, #64748b)' }}
            >
              ✕
            </button>
          </div>
          <form onSubmit={(e) => { e.preventDefault(); void fetchChecklists(); setIsFilterModalOpen(false); }} style={{ display: 'flex', flexDirection: 'column', gap: 20, padding: 24 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              
              <div className="filter-item">
                <label htmlFor="plate-input" style={{ fontWeight: 700, fontSize: 13, color: 'var(--tema-texto-2, #475569)' }}>Placa</label>
                <input
                  id="plate-input"
                  type="text"
                  placeholder="Ex: ABC1D23"
                  value={plate}
                  onChange={(e) => setPlate(e.target.value.toUpperCase())}
                  className="filter-input-field"
                />
              </div>

              <div className="filter-item">
                <label htmlFor="type-select" style={{ fontWeight: 700, fontSize: 13, color: 'var(--tema-texto-2, #475569)' }}>Tipo</label>
                <select
                  id="type-select"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className="filter-select-field"
                >
                  <option value="">Todos</option>
                  <option value="ENTRADA">Entrada</option>
                  <option value="SAIDA">Saída</option>
                  {/* at-96: Reparo e Retorno saíram (nunca foram usados); no lugar entraram os
                      dois tipos do retorno de garantia. */}
                  <option value="POS_EVENTO_ENTRADA">Entrada de pós-evento</option>
                  <option value="POS_EVENTO_SAIDA">Saída de pós-evento</option>
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="filter-item">
                  <label htmlFor="start-date-input" style={{ fontWeight: 700, fontSize: 13, color: 'var(--tema-texto-2, #475569)' }}>De (Data)</label>
                  <input
                    id="start-date-input"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="filter-input-field"
                  />
                </div>
                <div className="filter-item">
                  <label htmlFor="end-date-input" style={{ fontWeight: 700, fontSize: 13, color: 'var(--tema-texto-2, #475569)' }}>Até (Data)</label>
                  <input
                    id="end-date-input"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="filter-input-field"
                  />
                </div>
              </div>

            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, borderTop: '1px solid var(--tema-linha, #f1f5f9)', paddingTop: 16, marginTop: 12 }}>
              <button
                type="button"
                onClick={() => { handleClearFilters(); setIsFilterModalOpen(false); }}
                className="btn-filter-secondary"
              >
                Limpar Filtros
              </button>
              <button
                type="submit"
                className="btn-filter-primary"
              >
                Aplicar Filtros
              </button>
            </div>
          </form>
        </div>
      </div>
    )}

    {isChecklistOpen && selectedCarId && selectedSessionId && selectedSessionType && (
      <ChecklistModal
        carId={selectedCarId!}
        sessionId={selectedSessionId!}
        tipoServico={selectedSessionType!}
        isOpen={isChecklistOpen}
        onClose={() => setIsChecklistOpen(false)}
        sidebarRef={sidebarRef}
        readOnly={true}
        // at-65 (BUG-V11): "Checklists Realizados" é tela de consulta.
        permitirEdicao={false}
      />
    )}

    <ComoFunciona
      isOpen={isHelpOpen}
      onClose={() => setIsHelpOpen(false)}
      titulo="Como Funciona — Checklists Realizados"
      secoes={helpSections}
    />
    </>
  );
};

export default ChecklistHistoryPage;


